import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { LegalConsentFormComponent } from './legal-consent-form.component';
import { LegalConsentService } from '@core/services/data/legal-consent.service';
import { AuthService } from '@core/services/auth/auth.service';
import { TranslationService } from '../../../core/i18n/translation.service';
import { UIFeedbackService } from '../../services/ui-feedback.service';

/**
 * Blocking re-consent modal. Opened from the authenticated shell when
 * `LegalConsentService.needsConsent()` is true (existing account with no
 * consent, or a document version bump). `disableClose` — the user must
 * either accept the current documents or sign out.
 */
@Component({
  selector: 'app-reconsent-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    LegalConsentFormComponent,
    TranslatePipe,
  ],
  template: `
    <div class="reconsent">
      <header>
        <div class="hero-icon"><mat-icon>gavel</mat-icon></div>
        <h2 mat-dialog-title>{{ 'legal.reconsent.title' | translate }}</h2>
        <p class="lead">{{ 'legal.reconsent.lead' | translate }}</p>
      </header>

      <mat-dialog-content>
        <app-legal-consent-form
          [busy]="busy()"
          submitLabelKey="legal.reconsent.submit"
          (accepted)="onAccepted()"
        ></app-legal-consent-form>
      </mat-dialog-content>

      <footer class="reconsent-footer">
        <button
          type="button"
          mat-button
          [disabled]="busy()"
          (click)="onSignOut()"
        >
          <mat-icon>logout</mat-icon>
          {{ 'legal.reconsent.signOut' | translate }}
        </button>
      </footer>
    </div>
  `,
  styles: [`
    .reconsent { max-width: 560px; padding: 8px 4px; }
    header { text-align: center; display: grid; gap: 8px; margin-bottom: 8px; }
    .hero-icon {
      justify-self: center;
      display: inline-grid; place-items: center;
      width: 56px; height: 56px; border-radius: 50%;
      background: color-mix(in srgb, var(--color-primary, #3b82f6) 12%, transparent);
      mat-icon {
        font-size: 30px; width: 30px; height: 30px;
        color: var(--color-primary, #3b82f6);
      }
    }
    h2 { font-size: 22px; font-weight: 700; margin: 0; }
    .lead { color: var(--text-secondary, #6b7280); margin: 0; font-size: 14px; }
    .reconsent-footer { display: flex; justify-content: center; margin-top: 8px; }
  `],
})
export class ReconsentDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<ReconsentDialogComponent>);
  private readonly legalConsent = inject(LegalConsentService);
  private readonly authService = inject(AuthService);
  private readonly uiFeedback = inject(UIFeedbackService);
  private readonly translate = inject(TranslateService);
  private readonly i18n = inject(TranslationService);

  readonly busy = signal(false);

  onAccepted(): void {
    if (this.busy()) return;

    // Optimistic: flip local consent so `needsConsent()` is already false, then
    // dismiss the modal at once and persist in the background. A failed write
    // just leaves the recorded version stale, so the dialog reopens on the next
    // load — never a hard block, never a spinner wait.
    this.legalConsent.markLocallyAccepted();
    this.dialogRef.close(true);

    this.legalConsent
      .recordConsent({
        source: 'reconsent',
        locale: this.i18n.currentLanguage as 'en' | 'fr' | 'nl' | 'de',
      })
      .catch(err => {
        console.error('[reconsent] background save failed', err);
        this.uiFeedback.toast.error(
          this.translate.instant('legal.reconsent.error'),
        );
      });
  }

  async onSignOut(): Promise<void> {
    if (this.busy()) return;
    this.busy.set(true);
    await this.authService.logout();
    this.dialogRef.close(false);
  }
}
